import React, { useRef, MutableRefObject } from 'react';
import { useTooltipForLongName } from '../../hooks/useLongNameTooltip';
import {
  ButtonColor,
  ButtonVariant,
  Flex,
  FlexBehavior,
  FlexJustify,
  IconButton,
  Spinner,
  Tooltip,
} from 'react-magma-dom';
import { RelatedObject } from '../../types';
import { StyledDeleteModalButton } from '../Styled/StyledButtons';
import { StyledDeleteExplanation, StyledDeleteHeading, StyledDeleteTitle } from '../Styled/StyledWrappers';
import { StyledDeleteModalWindow } from '../Styled/StyledModal';
import { CloseModalIcon } from '../../assets/icons/ImageEditTool';

interface IDeleteModal {
  savedData: RelatedObject;
  isSuccessfullyDeleted: boolean;
  isLoading: boolean;
  mainColor: string;
  highlightColor: string;
  onClose: () => void;
  handleDeleteExemplar: (id: number) => void;
}

export const DeleteModal = (props: IDeleteModal): JSX.Element => {
  const { savedData, isSuccessfullyDeleted, isLoading, mainColor, highlightColor, handleDeleteExemplar, onClose } =
    props;
  const { id, title } = savedData;

  const styledDeleteExplanationRef = useRef() as MutableRefObject<HTMLSpanElement>;
  const styledTitleRef = useRef() as MutableRefObject<HTMLSpanElement>;

  const isTitleTooltip = useTooltipForLongName(styledDeleteExplanationRef, styledTitleRef);

  const showTitle = (withTooltip: boolean): JSX.Element => {
    const defaultTitleElement = (
      <StyledDeleteTitle ref={styledTitleRef} highlightColor={highlightColor}>
        {title}
      </StyledDeleteTitle>
    );

    const tooltipContainerStyle = {
      width: '100%',
    };

    const tooltipStyle = {
      minWidth: 'fit-content',
    };

    return withTooltip ? (
      <Tooltip
        testId="longname-modal-tooltip"
        containerStyle={tooltipContainerStyle}
        tooltipStyle={tooltipStyle}
        content={title}
      >
        {defaultTitleElement}
      </Tooltip>
    ) : (
      defaultTitleElement
    );
  };

  const renderLoader = (): JSX.Element => (
    <>
      <StyledDeleteHeading color="#000000" marginBottom="23px">
        <span>Deleting...</span>
      </StyledDeleteHeading>
      <Flex behavior={FlexBehavior.container} justify={FlexJustify.center}>
        <Spinner color={mainColor} size={50} />
      </Flex>
    </>
  );

  const renderSavedTextMessage = (): JSX.Element => (
    <>
      <StyledDeleteHeading color={mainColor}>
        <span>Success!</span>
      </StyledDeleteHeading>
      <StyledDeleteExplanation ref={styledDeleteExplanationRef}>
        {showTitle(isTitleTooltip)} has been successfully deleted!
      </StyledDeleteExplanation>
      <StyledDeleteModalButton onClick={onClose} color={mainColor}>
        <span>Done</span>
      </StyledDeleteModalButton>
    </>
  );

  const renderConfirmation = (): JSX.Element => (
    <>
      <StyledDeleteHeading color={mainColor}>
        <span>Are you sure?</span>
      </StyledDeleteHeading>
      <StyledDeleteExplanation ref={styledDeleteExplanationRef}>
        {showTitle(isTitleTooltip)} will be permanently deleted.
        <br />
        This action cannot be undone.
      </StyledDeleteExplanation>
      <StyledDeleteModalButton onClick={() => handleDeleteExemplar(id)} color={mainColor}>
        <span>Yes, Delete</span>
      </StyledDeleteModalButton>
    </>
  );

  return (
    <StyledDeleteModalWindow isOpen isCloseButtonHidden onClose={onClose}>
      {!isLoading && (
        <IconButton
          aria-label="Close delete exemplar modal window"
          color={ButtonColor.secondary}
          variant={ButtonVariant.link}
          icon={<CloseModalIcon />}
          onClick={onClose}
        />
      )}
      {isLoading ? renderLoader() : isSuccessfullyDeleted ? renderSavedTextMessage() : renderConfirmation()}
    </StyledDeleteModalWindow>
  );
};
